import React, {
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { AuthContext } from "../../context/AuthContext";
import { useSnackbar } from "notistack";
import axios from "axios";
import {
  Box,
  Backdrop,
  CircularProgress,
  Typography,
  ButtonGroup,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Pagination,
  Chip,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import useForm from "../../shared/hooks/form-hooks";

import Grid from "@mui/material/Unstable_Grid2";
import { Download, Print } from "@mui/icons-material";
import { font as shabnamFontBinary } from "../../shared/util/functions";
import Input from "../../shared/components/Input";
import jMoment from "moment-jalaali";
import { VALIDATOR_AUTOCOMPLETE } from "../../shared/util/validators";

const columns = [
  { label: "شماره جعبه", key: "boxNumber", alignment: "center" },
  { label: "نام بیمار", key: "patientName", alignment: "center" },
  { label: "نام پزشک", key: "doctorName", alignment: "center" },
  { label: "نوع کار", key: "workType", alignment: "center" },
  { label: "مرحله کاری", key: "workState", alignment: "center" },
  { label: "تاریخ آخرین ورود", key: "lastEnterDate", alignment: "center" },
  {
    label: "تاریخ پیش‌بینی خروج",
    key: "exitDatePredict",
    alignment: "center",
  },
  { label: "وضعیت", key: "status", alignment: "center" },
];

const statuses = [
  { name: "دیر شده", value: "late" },
  { name: "ارسال امروز", value: "forToday" },
  { name: "ارسال فردا", value: "forTommorow" },
  { name: "دو روز دیگر", value: "forTwoDays" },
  { name: "سه روز دیگر", value: "forThreeDays" },
  { name: "وقت دارد", value: "notLate" },
  { name: "در انتظار", value: "inQuee" },
];

const WorksTablePlanning = () => {
  const [isTableInitialized, setIsTableInitialized] = useState(false);
  const [rawData, setRawData] = useState([]);
  const [visibleRows, setVisibleRows] = useState([]);

  const [orderTypes, setOrderTypes] = useState([]);

  const [orderTypeProcesses, setOrderTypeProcesses] = useState([]);

  const [pageCount, setPageCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const auth = useContext(AuthContext);
  const [formState, inputHandler] = useForm({
    orderTypeProcessId: { value: null, isValid: false },
    orderTypeId: { value: null, isValid: false },
    delation: { value: null, isValid: false },
    order: { value: "asc", isValid: true },
    orderBy: { value: "boxNumber", isValid: true },
    page: { value: 1, isValid: true },
    rowCount: { value: null, isValid: false },
  });
  const { enqueueSnackbar } = useSnackbar();

  const handleRequestSort = (_, property) => {
    const isAsc =
      formState.inputs.orderBy.value === property &&
      formState.inputs.order.value === "asc";
    inputHandler("order", isAsc ? "desc" : "asc", true);
    inputHandler("orderBy", property, true);
  };

  const handlePageChange = (_, n) => {
    inputHandler("page", n - 1, n - 1 > 0);
  };

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const getComparator = useCallback((order, orderBy) => {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }, []);

  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  const createSortHandler = (property) => (event) => {
    handleRequestSort(event, property);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    formState.inputs.page.value > 0
      ? Math.max(
          0,
          (1 + formState.inputs.page.value) * formState.inputs.rowCount.value -
            visibleRows.length
        )
      : 0;

  useMemo(() => {
    setVisibleRows(
      stableSort(
        rawData,
        getComparator(
          formState.inputs.order.value,
          formState.inputs.orderBy.value
        )
      )
    );
  }, [
    formState.inputs.order.value,
    formState.inputs.orderBy.value,
    getComparator,
    rawData,
  ]);

  const getOrderTypes = async () => {
    await axios
      .post(
        "/api/v1/laboratorys/get-orderTypes",
        {
          page: 1,
          rowCount: 10,
          onlyName: false,
        },
        { headers: { authorization: auth.token } }
      )
      .then((res) => {
        setOrderTypes(res.data.data.result);
      })
      .catch((e) => {
        const messages = e.response.data.data;
        Array.isArray(messages)
          ? messages.map((message) => {
              enqueueSnackbar(message, { variant: "error" });
            })
          : enqueueSnackbar(messages, { variant: "error" });
      });
  };

  const getOrderTypeProcesses = async () => {
    setIsLoading(true);
    await axios
      .post(
        "/api/v1/laboratorys/get-orderType-process",
        { orderTypeId: formState.inputs.orderTypeId.value.id },
        { headers: { Authorization: auth.token } }
      )
      .then((res) => {
        setOrderTypeProcesses(res.data.data);
      })
      .catch((e) => {
        const messages = e.response.data.data;
        Array.isArray(messages)
          ? messages.map((message) => {
              enqueueSnackbar(message, { variant: "error" });
            })
          : enqueueSnackbar(messages, { variant: "error" });
      });
    setIsLoading(false);
  };

  const getTableData = async () => {
    setIsLoading(true);
    await axios
      .post(
        "/api/v1/laboratorys/get-planning-request",
        {
          ...(formState.inputs.delation.value !== "inQuee" && {
            orderTypeProcessIds: [
              formState.inputs.orderTypeProcessId.value?.id ?? 1,
            ],
          }),
          orderTypeIds: [formState.inputs.orderTypeId.value?.id ?? 1],
          delation: [formState.inputs.delation.value?.value ?? "late"],
          page: formState.inputs.page.value,
          rowCount: formState.inputs.rowCount.value,
        },
        {
          headers: {
            authorization: auth.token,
          },
        }
      )
      .then((response) => {
        setPageCount(response.data.data.pages);
        setRawData(response.data.data.result);
        setIsTableInitialized(true);
      })
      .catch((e) => {
        const messages = e.response.data.data;
        Array.isArray(messages)
          ? messages.map((message) => {
              enqueueSnackbar(message, { variant: "error" });
            })
          : enqueueSnackbar(messages, { variant: "error" });
      });
    setIsLoading(false);
  };

  useEffect(() => {
    getOrderTypes();
    if (isTableInitialized) {
      getTableData();
    }
  }, [formState.inputs.page.value]);

  useEffect(() => {
    if (formState.inputs.orderTypeId.value) {
      getOrderTypeProcesses();
    }
    inputHandler("orderTypeProcessId", null, false);
  }, [formState.inputs.orderTypeId.value]);

  const onClickButton = () => {
    if (isTableInitialized) setIsTableInitialized(true);
    setVisibleRows([]);
    getTableData();
  };

  const getStatusTableCell = (status) => (
    <Chip
      label={getStatusInfo(status).label}
      color={getStatusInfo(status).color}
      sx={{ width: "100%" }}
    />
  );

  const getStatusInfo = (status) => {
    let label = "وضعیت";
    let color = "primary";
    switch (status) {
      case "late":
        label = "دیر شده";
        color = "error";
        break;
      case "forToday":
        label = "ارسال امروز";
        color = "warning";
        break;
      case "forTommorow":
        label = "ارسال فردا";
        color = "secondary";
        break;
      case "forTwoDays":
        label = "دو روز دیگر";
        color = "primary";
        break;
      case "forThreeDays":
        label = "سه روز دیگر";
        color = "primary";
        break;
      case "notLate":
        label = "وقت دارد";
        color = "success";
        break;
      case "inQuee":
      default:
        label = "در انتظار";
        color = "inherit";
        break;
    }
    return { label, color };
  };

  const handleChangeRowsPerPage = (n) => {
    inputHandler("rowCount", n, true);
    inputHandler("page", 1, true);
  };

  const handlePDFDocCreation = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "px" });
    doc.setFont("Shabnam-FD");
    doc.output();
    // Assuming columns are in the correct order
    autoTable(doc, {
      head: [columns.map((col) => col.label)], // No reverse here
      headStyles: {
        fontStyle: "bold",
        font: "Shabnam-FD",
        halign: "center",
        fillColor: "#000",
      },
      theme: "striped",
      styles: {
        font: "Shabnam-FD",
        halign: "center",
        valign: "middle",
        fontSize: 12,
        lineWidth: 1,
      },
      body: visibleRows.map((row) => {
        return [
          row.boxNumber,
          row.patiantName,
          row.doctor.name,
          row.orderType.name,
          orderTypeProcesses.find(
            (typeProcess) =>
              typeProcess.id === row.orderProcess.orderTypeProcessId
          ).stationName,
          jMoment(parseInt(row.orderProcess.startDate)).format("jYYYY/jMM/jDD"),
          jMoment(parseInt(row.orderProcess.predictionDate)).format(
            "jYYYY/jMM/jDD"
          ),
          getStatusInfo(row.delation).label,
        ];
      }),
      alternateRowStyles: {
        // Optional: Set a specific fill color if needed
      },
      bodyStyles: {
        minCellHeight: 24,
      },
    });
    return doc;
  };

  const handlePDFDownload = (doc, _fileName) => {
    const d = new Date();
    doc.save(
      _fileName
        ? _fileName
        : `report-${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${d.getHours()}-${d.getMinutes()}-${d.getSeconds()}`
    );
  };

  const handlePDFPrint = (doc) => {
    doc.autoPrint();
    const hiddenFrame = document.createElement("iframe");
    hiddenFrame.style.position = "fixed";
    hiddenFrame.style.width = "1px";
    hiddenFrame.style.height = "1px";
    hiddenFrame.style.opacity = "0.01";

    const isSafari = /^((?!chrome|android).)*safari/i.test(
      window.navigator.userAgent
    );

    if (isSafari) {
      try {
        hiddenFrame.contentWindow.document.execCommand("print", false, null);
      } catch (e) {
        hiddenFrame.contentWindow.print();
      }
    }
    hiddenFrame.src = doc.output("bloburl");
    document.body.appendChild(hiddenFrame);
  };

  return (
    <Grid container spacing={4} width={"100%"}>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Grid xs={12} sx={{ display: "flex", alignItems: "flex-start" }}>
        <Typography variant="h5" gutterBottom>
          جدول برنامه ریزی
        </Typography>
      </Grid>
      <Grid xs={12} px={0} spacing={2}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onClickButton();
          }}
        >
          <Grid
            container
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
            }}
            spacing={2}
          >
            <Grid
              xs={12}
              md={6}
              container
              spacing={1}
              sx={{ alignItems: "center" }}
            >
              <Grid xs={12} sm={4} lg={3} textAlign={"start"}>
                <Typography variant="subtitle1">تعداد سطرهای جدول:</Typography>
              </Grid>
              <Grid xs={12} sm={8} lg={9}>
                <ButtonGroup fullWidth orientation="horizontal">
                  <Button
                    onClick={() => handleChangeRowsPerPage(25)}
                    variant={
                      formState.inputs.rowCount.value === 25
                        ? "contained"
                        : "outlined"
                    }
                  >
                    25
                  </Button>
                  <Button
                    onClick={() => handleChangeRowsPerPage(50)}
                    variant={
                      formState.inputs.rowCount.value === 50
                        ? "contained"
                        : "outlined"
                    }
                  >
                    50
                  </Button>
                  <Button
                    onClick={() => handleChangeRowsPerPage(100)}
                    variant={
                      formState.inputs.rowCount.value === 100
                        ? "contained"
                        : "outlined"
                    }
                  >
                    100
                  </Button>
                </ButtonGroup>
              </Grid>
            </Grid>

            <Grid xs={12} md={6} lg={2}>
              <Input
                id="delation"
                element="autoComplete"
                label="وضعیت کار"
                validators={[VALIDATOR_AUTOCOMPLETE()]}
                errorText="لطفا وضعیت کار را انتخاب کنید."
                onInput={inputHandler}
                options={statuses}
                getOptionLabel={(s) => s.name}
                inputValid={true}
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid xs={12} md={6} lg={2}>
              <Input
                id="orderTypeId"
                element="autoComplete"
                label="نوع کار"
                validators={[VALIDATOR_AUTOCOMPLETE()]}
                errorText="لطفا نوع کار را انتخاب کنید."
                onInput={inputHandler}
                options={orderTypes}
                getOptionLabel={(option) => option.name}
                inputValid={true}
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid xs={12} md={6} lg={2}>
              <Input
                id="orderTypeProcessId"
                element="autoComplete"
                label="مرحله کار"
                validators={[VALIDATOR_AUTOCOMPLETE()]}
                errorText="لطفا مرحله کار را انتخاب کنید."
                onInput={inputHandler}
                options={orderTypeProcesses}
                getOptionLabel={(option) => option.stationName}
                inputValid={true}
                size="small"
                variant="outlined"
                disabled={!formState.inputs.orderTypeId.value}
              />
            </Grid>
            <Grid xs={12}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                type="submit"
                disabled={!formState.isValid}
              >
                نمایش جدول
              </Button>
            </Grid>
          </Grid>
        </form>
      </Grid>
      <Grid xs={12} container spacing={2} px={0}>
        <Grid xs={12} md={6} alignSelf={"center"}>
          <Typography paragraph textAlign="start" mb={0}>
            تعداد رکوردهای انتخاب شده: {formState.inputs.rowCount.value}
          </Typography>
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <Button
            fullWidth
            disabled={!formState.inputs.rowCount.value || !visibleRows.length}
            variant="contained"
            startIcon={<Download />}
            onClick={() => handlePDFDownload(handlePDFDocCreation())}
          >
            دانلود گزارش
          </Button>
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <Button
            fullWidth
            disabled={!formState.inputs.rowCount.value || !visibleRows.length}
            variant="contained"
            startIcon={<Print />}
            color="secondary"
            onClick={() => handlePDFPrint(handlePDFDocCreation())}
          >
            چاپ گزارش
          </Button>
        </Grid>
      </Grid>
      <Grid xs={12} px={0}>
        {!isTableInitialized ? (
          <Typography variant="h6">
            تنظیمات نمایش جدول را انتخاب کنید.
          </Typography>
        ) : visibleRows.length ? (
          <TableContainer sx={{ maxHeight: "720px" }}>
            <Table stickyHeader id="jspdf-target-table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.alignment}
                      sortDirection={
                        formState.inputs.orderBy.value === column.key
                          ? formState.inputs.order.value
                          : "asc"
                      }
                    >
                      <TableSortLabel
                        active={formState.inputs.orderBy.value === column.key}
                        direction={
                          formState.inputs.orderBy.value === column.key
                            ? formState.inputs.order.value
                            : "asc"
                        }
                        onClick={createSortHandler(column.key)}
                      >
                        {column.label}
                        {formState.inputs.orderBy.value === column.key ? (
                          <Box component="span" sx={visuallyHidden}>
                            {formState.inputs.order.value === "desc"
                              ? "sorted descending"
                              : "sorted ascending"}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row) => {
                  return (
                    <TableRow key={row.id}>
                      <TableCell align="center">{row.boxNumber}</TableCell>
                      <TableCell align="center">{row.patiantName}</TableCell>
                      <TableCell align="center">{row.doctor.name}</TableCell>
                      <TableCell align="center">{row.orderType.name}</TableCell>
                      <TableCell align="center">
                        {/* {
													orderTypeProcesses.find(
														(typeProcess) =>
															typeProcess.id ===
															row.orderProcess
																.orderTypeProcessId
													).stationName
												} */}
                        {row.processTypes.map((station) => {
                          return <p>{station.stationName}</p>;
                        })}
                      </TableCell>
                      <TableCell align="center">
                        {jMoment(parseInt(row.orderProcess.startDate)).format(
                          "jYYYY/jMM/jDD"
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {jMoment(
                          parseInt(row.orderProcess.predictionDate)
                        ).format("jYYYY/jMM/jDD")}
                      </TableCell>
                      <TableCell align="center">
                        {getStatusTableCell(row.delation)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="h6">رکوردی برای نمایش پیدا نشد.</Typography>
        )}
      </Grid>
      {pageCount > 0 && (
        <Grid xs={12} pl={0} sx={{ display: "flex", justifyContent: "center" }}>
          <Pagination count={pageCount} onChange={handlePageChange} />
        </Grid>
      )}
    </Grid>
  );
};

export default WorksTablePlanning;

//add font to jspdf class

const font = shabnamFontBinary;

var callAddFont = function () {
  this.addFileToVFS("Shabnam-FD-normal.ttf", font);
  this.addFont("Shabnam-FD-normal.ttf", "Shabnam-FD", "normal");
};
jsPDF.API.events.push(["addFonts", callAddFont]);
